/* PW DIRECT REQUEST START */
(() => {
// Shared with the browser. These helpers never trust browser prices or identity.
function requestFromCart(cart, requestId) {
  if (!cart?.items?.length) throw new Error('Je winkelmand is leeg.');
  const items = cart.items.map(item => {
    if (item.selling_plan_allocation || item.parent_relationship || item.item_components?.length || Object.keys(item.properties || {}).length) {
      throw new Error('Je winkelmand bevat een pakket, abonnement of artikel met extra opties. Neem contact met ons op om dit samen aan te vragen.');
    }
    if (!/^[1-9][0-9]*$/.test(String(item.variant_id)) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) throw new Error('Ongeldig artikel in je winkelmand.');
    return { variantId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity };
  });
  return { requestId, items, discountCodes: (cart.discount_codes || []).map(d => d.code).filter(c => typeof c === 'string' && c.trim()), note: cart.note || '' };
}

function cartSnapshot(cart) {
  return { token: cart.token, items: cart.items.map(i => ({ key: i.key, variantId: String(i.variant_id), quantity: i.quantity,
    title: i.product_title || i.title })), note: cart.note || '', discountCodes: (cart.discount_codes || []).map(d => d.code).sort() };
}

function cartSignature(snapshot) {
  return JSON.stringify({ token: snapshot.token, items: snapshot.items.map(({ key, variantId, quantity }) => ({ key, variantId, quantity })).sort((a,b) => a.key.localeCompare(b.key)),
    note: snapshot.note, discountCodes: snapshot.discountCodes });
}

function cleanupPlan(snapshot, current) {
  // Never match by variant alone: discount changes can change line keys. If any
  // submitted line changed, retain the cart and ask the buyer to check it.
  if (!snapshot.token || current.token !== snapshot.token) return { safe: false, updates: {} };
  const updates = {};
  for (const line of snapshot.items) {
    const found = current.items.filter(i => i.key === line.key && String(i.variant_id) === line.variantId);
    if (found.length !== 1 || found[0].quantity !== line.quantity) return { safe: false, updates: {} };
    updates[line.key] = 0;
  }
  return { safe: true, updates };
}


// Called under the shared browser lock. Identity and prices are verified again
// by the server; the saved ID is only for safe retries after a lost response.
async function sendCartRequest({ context, cart, storage, jsonFetch, prefix, cartRoot, uuid, notify }) {
  if (!context.customerId || !context.csrfToken) throw new Error('Je klantaccount kon niet worden gecontroleerd. Vernieuw de pagina.');
  const key = `pw-quote-v2:${context.customerId}`;
  let saved = JSON.parse(storage.getItem(key) || 'null');
  const persist = () => storage.setItem(key, JSON.stringify(saved));
  if (saved && (!saved.request?.requestId || !Array.isArray(saved.snapshot?.items))) {
    throw new Error('Je bewaarde aanvraag kan niet worden gelezen. Neem contact met ons op voordat je opnieuw indient.');
  }
  let previousRequestId;
  if (saved?.status === 'sent') {
    const released = saved.cartReleased || storage.getItem(`pw-quote-cleanup:${saved.reference}`) === 'done';
    const submittedGone = !cart.items.some(item => saved.snapshot.items.some(line => line.variantId === String(item.variant_id)));
    if (!cart.items.length || (!released && !submittedGone)) return { saved, key, restored: true };
    previousRequestId = saved.request.requestId;
    saved = null;
  }
  if (saved && ['review','rejected'].includes(saved.status)) return { saved, key, restored: true };
  if (!context.profile?.company || context.profile.needsAddress) return { needsProfile: true };
  if (!saved) {
    saved = { request: requestFromCart(cart, uuid()), snapshot: cartSnapshot(cart), status: 'pending' };
    if (previousRequestId) saved.request.previousRequestId = previousRequestId;
    persist(); // Must succeed before any order/email can be created.
  }
  notify?.(saved);
  let result;
  try {
    result = await jsonFetch(`${prefix}/request`, { method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ csrfToken: context.csrfToken, request: saved.request }) });
  } catch {
    return { saved: { ...saved, message: 'De ontvangst is nog niet bevestigd. Klik op Status controleren; je aanvraagcode blijft behouden.' }, key };
  }
  if (!['sent','pending','review','rejected'].includes(result.status)) {
    return { saved: { ...saved, message: result.message || 'Controleer de status opnieuw; je aanvraagcode blijft behouden.' }, key };
  }
  Object.assign(saved, { status: result.status, reference: result.reference, message: result.message });
  try { persist(); } catch {
    // In-memory success must not turn into a generic failure if storage fills up.
    return { saved: { ...saved, cleanupMessage: 'Controleer je winkelmand voordat je opnieuw bestelt.' }, key };
  }
  if (saved.status !== 'sent') return { saved, key };
  try {
    const cleanupKey = `pw-quote-cleanup:${saved.reference}`;
    if (!saved.reference || storage.getItem(cleanupKey)) {
      saved.cleanupMessage = 'Controleer je winkelmand voordat je opnieuw bestelt.';
    } else {
      const current = await jsonFetch(`${cartRoot}cart.js`);
      const plan = cleanupPlan(saved.snapshot, current);
      if (!plan.safe) {
        saved.cleanupMessage = 'Je winkelmand is intussen gewijzigd en daarom behouden. Verwijder de aangevraagde artikelen zelf als je ze niet nogmaals wilt bestellen.';
      } else {
        storage.setItem(cleanupKey, 'attempted');
        const updated = await jsonFetch(`${cartRoot}cart/update.js`, { method:'POST', headers:{'content-type':'application/json'}, body:JSON.stringify({ updates:plan.updates }) });
        if (!Array.isArray(updated.items) || updated.items.some(item => Object.hasOwn(plan.updates, item.key))) throw new Error('Cleanup not confirmed');
        storage.setItem(cleanupKey, 'done');
        saved.cartReleased = true;
        saved.cleanupMessage = 'De aangevraagde artikelen zijn uit je winkelmand verwijderd.';
      }
    }
  } catch {
    saved.cleanupMessage = 'Het bijwerken van je winkelmand kon niet worden bevestigd. Controleer deze voordat je opnieuw bestelt.';
  }
  try { persist(); } catch { /* Success remains visible even if storage is unavailable. */ }
  return { saved, key };
}

(() => {
  if (window.PWCartRequest) return;
  const prefix = '/apps/pw-prijsaanvraag';
  const root = () => window.Shopify?.routes?.root || '/';
  let busy = false, view = null, requestKey = null;
  const originalLabels = new WeakMap();
  const jsonFetch = async (url, options = {}) => {
    const response = await fetch(url, { ...options, credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(60_000) });
    let result;
    try { result = await response.json(); } catch { throw new Error('Geen geldig antwoord ontvangen. Probeer het opnieuw.'); }
    if (!response.ok && !['sent','pending','review','rejected'].includes(result.status)) throw new Error(result.message || 'De verbinding is tijdelijk niet beschikbaar.');
    return result;
  };
  function draw(event) {
    if (!busy && view?.status === 'sent' && view.cartReleased && event?.detail?.cart?.items?.length) {
      view = null; requestKey = null;
      document.querySelectorAll('[data-pw-request-status]').forEach(el => el.remove());
    }
    for (const button of document.querySelectorAll('[data-pw-request-checkout]')) {
      const label = button.querySelector('.button__text') || button;
      if (!originalLabels.has(label)) originalLabels.set(label, label.textContent);
      label.textContent = busy ? 'Aanvraag verwerken…' : view?.status === 'sent' ? '✓ Aanvraag verzonden' : view?.status === 'pending' ? 'Status controleren' : view?.status === 'review' ? 'Aanvraag wordt gecontroleerd' : originalLabels.get(label);
      button.setAttribute('aria-busy', String(busy));
      button.dataset.pwRequestBusy = String(busy);
    }
    if (!view) return;
    for (const scope of document.querySelectorAll('[data-pw-cart-drawer], #main-cart')) {
      let box = scope.querySelector('[data-pw-request-status]');
      if (!box) {
        box = document.createElement('div'); box.dataset.pwRequestStatus = '';
        box.setAttribute('role','status'); box.setAttribute('aria-live','polite');
        const body = scope.querySelector('.sidebar__body') || scope.querySelector('main') || scope;
        body.prepend(box);
      }
      box.dataset.pwRequestState = view.status || 'message';
      box.replaceChildren();
      const text = document.createElement('p');
      if (view.status === 'sent') {
        const title = document.createElement('strong');
        title.textContent = '✓ Aanvraag verzonden'; box.append(title);
        text.textContent = 'De betaaluitnodiging is per e-mail verstuurd.';
      } else {
        text.textContent = [view.message, view.cleanupMessage].filter(Boolean).join(' ');
      }
      box.append(text);
      if (view.status === 'sent' && view.reference) {
        const reference = document.createElement('small'); reference.textContent = `Aanvraagnummer: ${view.reference}`; box.append(reference);
      }
      if (view.status === 'sent' && !view.cartReleased && view.cleanupMessage) {
        const warning = document.createElement('p'); warning.textContent = view.cleanupMessage; box.append(warning);
      }
      if (view.status !== 'sent' && view.snapshot?.items?.length) {
        const details = document.createElement('details'), summary = document.createElement('summary'), list = document.createElement('ul');
        summary.textContent = 'Artikelen in deze aanvraag'; details.append(summary);
        for (const item of view.snapshot.items) { const li = document.createElement('li'); li.textContent = `${item.quantity} × ${item.title}`; list.append(li); }
        details.append(list); box.append(details);
      }
      if (view.status === 'rejected' && requestKey) {
        const reset = document.createElement('button'); reset.type = 'button'; reset.textContent = 'Aanvraag aanpassen';
        reset.disabled = busy;
        reset.addEventListener('click', async () => {
          if (busy) return;
          try {
            await navigator.locks.request('pw-price-request', async () => {
              const saved = JSON.parse(localStorage.getItem(requestKey) || 'null');
              if (saved && !['sent','rejected'].includes(saved.status)) throw new Error('Er loopt nog een aanvraag. Controleer eerst de status.');
              localStorage.removeItem(requestKey); view = null; requestKey = null;
              document.querySelectorAll('[data-pw-request-status]').forEach(el => el.remove()); draw();
            });
          } catch (error) { view = { message:error.message }; draw(); }
        });
        box.append(reset);
      }
    }
  }
  async function submit(button) {
    if (busy) return;
    const scope = button.closest('[data-pw-cart-drawer], #main-cart') || document;
    const terms = scope.querySelector('input[name="terms"], #rterms-0');
    if (terms && !terms.checked) { terms.focus(); terms.reportValidity(); view = { message:'Accepteer eerst de algemene voorwaarden.' }; draw(); return; }
    busy = true; draw();
    try {
      if (!navigator.locks?.request) throw new Error('Gebruik een recente browser om je aanvraag te versturen.');
      await navigator.locks.request('pw-price-request', async () => {
        let context;
        try { context = await jsonFetch(`${prefix}/context`); } catch (error) {
          if (error.message === 'Log eerst in') { window.location.assign(`${prefix}/aanvraag`); return; }
          throw error;
        }
        if (!context.profile?.company || context.profile.needsAddress) { window.location.assign(`${prefix}/aanvraag`); return; }
        const noteField = scope.querySelector('textarea[name="note"]');
        const note = noteField?.value;
        const noteElement = noteField?.closest('cart-note');
        if (noteElement?.flushPendingSave) await noteElement.flushPendingSave();
        const work = async () => {
          const cart = await jsonFetch(`${root()}cart.js`);
          if (note !== undefined) cart.note = note;
          const result = await sendCartRequest({ context, cart, storage:localStorage, jsonFetch, prefix, cartRoot:root(), uuid:() => crypto.randomUUID(),
            notify:saved => { view = { ...saved, message:'Je aanvraag wordt verwerkt. Een moment geduld.' }; draw(); } });
          if (result.needsProfile) { window.location.assign(`${prefix}/aanvraag`); return; }
          view = result.saved; requestKey = result.key; draw();
        };
        if (window.PWCart?.enqueue) await window.PWCart.enqueue(work); else await work();
      });
      if (view?.status === 'sent' && window.PWCart?.refresh) {
        try { await window.PWCart.refresh({ openDrawer:false }); } catch { /* Keep success visible even if section rendering fails. */ }
      }
    } catch (error) {
      view = { ...view, message: view?.status === 'sent' ? 'Je aanvraag is verzonden. Controleer je e-mail en je winkelmand.' : error.message };
    } finally { busy = false; draw(); }
  }
  // Window capture runs before the theme's document/drawer navigation handlers.
  window.addEventListener('click', event => {
    const button = event.target.closest?.('[data-pw-request-checkout]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (button.getAttribute('aria-disabled') === 'true' || button.classList.contains('disabled')) return;
    void submit(button);
  }, true);
  document.addEventListener('pw:cart:updated', draw);
  document.addEventListener('pw:cart-drawer:rendered', draw);
  const style = document.createElement('style');
  style.textContent = `[data-pw-request-checkout]:hover{background:#163421!important;color:#fff!important}[data-pw-request-checkout][data-pw-request-busy="true"]{cursor:wait}[data-pw-request-checkout][data-pw-request-busy="true"]::before{content:"";display:inline-block;width:16px;height:16px;margin-right:9px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:pw-request-spin .8s linear infinite}[data-pw-request-status]{box-sizing:border-box;padding:14px 16px;margin:16px 0;background:#f1f5ef;color:#26392c;border:1px solid #d5dfd2;border-radius:6px;font-size:14px;line-height:1.5;overflow-wrap:anywhere}[data-pw-cart-drawer] .sidebar__body>[data-pw-request-status]{margin:16px 20px}[data-pw-request-status] strong{display:block;margin-bottom:4px;font-size:15px}[data-pw-request-status] p{margin:0}[data-pw-request-status] p+p{margin-top:8px}[data-pw-request-status] small{display:block;margin-top:6px;font-size:12px}[data-pw-request-status] button{margin-top:10px;padding:9px 12px;border:1px solid #284c37;border-radius:4px;background:white;color:#284c37;font:inherit;cursor:pointer}[data-pw-request-status] button:hover{background:#163421;color:white}[data-pw-request-status] summary{cursor:pointer}[data-pw-request-status] ul{padding-left:18px}@keyframes pw-request-spin{to{transform:rotate(360deg)}}@media(prefers-reduced-motion:reduce){[data-pw-request-checkout]::before{animation:none!important}}`;
  document.head.append(style);
  window.PWCartRequest = { draw };
})();

})();
/* PW DIRECT REQUEST END */
(() => {
  if (window.__PW_CART_ENGINE_LOADED__) return;
  window.__PW_CART_ENGINE_LOADED__ = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-pw-request-checkout], [data-cart-checkout-page-button]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    const scope = button.closest('[data-pw-cart-drawer], #main-cart') || document;
    const terms = scope.querySelector('input[name="terms"], #rterms-0');
    if (terms && !terms.checked) { terms.focus(); terms.reportValidity(); return; }
    window.location.assign(button.getAttribute('href') || '/checkout');
  }, true);

  const sectionNames = {
    drawer: 'cart-drawer',
    main: 'main-cart'
  };

  let mutationQueue = Promise.resolve();

  const route = (path) => {
    const configuredRoot = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';
    const root = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
    return `${root}${String(path).replace(/^\/+/, '')}`;
  };

  const parseSection = (html) => {
    if (typeof html !== 'string' || html.trim() === '') return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const requestError = new Error(
        (data && (data.description || data.message)) || `Cart request failed (${response.status})`
      );
      requestError.status = response.status;
      requestError.data = data;
      throw requestError;
    }

    return data;
  };

  const setText = (element, value) => {
    if (!element || value === null || typeof value === 'undefined') return;
    const next = String(value).trim();
    if (element.textContent.trim() !== next) element.textContent = next;
  };

  const updateGlobalCartMeta = (cart, sourceDocument) => {
    const sourceCount = sourceDocument && sourceDocument.querySelector('[data-cart-count]');
    const sourceTotal = sourceDocument && sourceDocument.querySelector('[data-cart-total]');
    const itemCount = cart && Number.isFinite(Number(cart.item_count))
      ? Number(cart.item_count)
      : sourceCount
        ? Number.parseInt(sourceCount.textContent, 10)
        : null;
    const totalText = sourceTotal ? sourceTotal.textContent.trim() : null;

    if (itemCount !== null && Number.isFinite(itemCount)) {
      document.querySelectorAll('[data-header-cart-count], [data-cart-count], [data-cart-count-number]')
        .forEach((element) => setText(element, itemCount));
    }

    if (totalText !== null) {
      document.querySelectorAll('[data-header-cart-total], [data-cart-total], [data-cart-subtotal]')
        .forEach((element) => setText(element, totalText));
    }

    if (cart && Number.isFinite(Number(cart.total_price))) {
      document.querySelectorAll('[data-cart-total-cents]')
        .forEach((element) => setText(element, cart.total_price));
    }

    document.querySelectorAll('.cart__form').forEach((form) => {
      form.classList.toggle('cart--empty', itemCount === 0);
    });

    document.querySelectorAll('[data-js-site-cart-sidebar]').forEach((drawer) => {
      drawer.classList.toggle('cart-is-empty', itemCount === 0);
      if (itemCount !== null && Number.isFinite(itemCount)) {
        drawer.dataset.cartItems = String(itemCount);
      }
    });

    document.querySelectorAll('[data-cart-checkout-button]').forEach((button) => {
      const disabled = itemCount === 0;
      button.classList.toggle('disabled', disabled);
      if (disabled) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  };

  const initializeDrawer = (root) => {
    if (!root) return;

    const run = () => {
      if (window.PWCartDrawer && typeof window.PWCartDrawer.init === 'function') {
        window.PWCartDrawer.init(root);
      }
      document.dispatchEvent(new CustomEvent('pw:cart-drawer:rendered', {
        detail: { root }
      }));
    };

    if (window.PWCartDrawer && typeof window.PWCartDrawer.init === 'function') {
      run();
      return;
    }

    const source = root.dataset.controllerSrc;
    if (!source) {
      run();
      return;
    }

    const existing = [...document.scripts].find((script) => script.src === source);
    if (existing) {
      if (existing.dataset.pwLoaded === 'true') run();
      else existing.addEventListener('load', run, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = source;
    script.defer = true;
    script.dataset.pwDynamicDrawerController = 'true';
    script.addEventListener('load', () => {
      script.dataset.pwLoaded = 'true';
      run();
    }, { once: true });
    document.head.appendChild(script);
  };

  const activateExternalScripts = (scope) => {
    if (!scope) return;

    scope.querySelectorAll('script[src]').forEach((inertScript) => {
      const source = inertScript.src;
      if (!source) return;

      const alreadyActive = [...document.scripts].some((script) => {
        return script !== inertScript
          && script.src === source
          && script.dataset.pwInertSectionScript !== 'true';
      });

      inertScript.dataset.pwInertSectionScript = 'true';
      inertScript.remove();
      if (alreadyActive) return;

      const script = document.createElement('script');
      script.src = source;
      script.async = false;
      script.dataset.pwActivatedSectionScript = 'true';
      document.head.appendChild(script);
    });
  };

  const renderDrawer = (html, cart) => {
    const parsed = parseSection(html);
    if (!parsed) return null;

    const nextDrawer = parsed.querySelector('.pw-cart-drawer');
    if (!nextDrawer) return parsed;

    let currentDrawer = nextDrawer.id
      ? document.getElementById(nextDrawer.id)
      : document.querySelector('.pw-cart-drawer');

    if (!currentDrawer) {
      const host = document.querySelector('[data-js-site-cart-sidebar]');
      const nextSection = parsed.querySelector(`#shopify-section-${sectionNames.drawer}`);
      const currentSection = host && host.querySelector(`#shopify-section-${sectionNames.drawer}`);

      if (currentSection && nextSection) {
        currentSection.innerHTML = nextSection.innerHTML;
        activateExternalScripts(currentSection);
        currentDrawer = currentSection.querySelector('.pw-cart-drawer');
      } else if (host && nextSection) {
        const insertedSection = document.importNode(nextSection, true);
        host.appendChild(insertedSection);
        activateExternalScripts(insertedSection);
        currentDrawer = host.querySelector('.pw-cart-drawer');
      } else if (host) {
        const insertedDrawer = document.importNode(nextDrawer, true);
        host.appendChild(insertedDrawer);
        activateExternalScripts(insertedDrawer);
        currentDrawer = host.querySelector('.pw-cart-drawer');
      }

      if (!currentDrawer) return parsed;
      initializeDrawer(currentDrawer);
      updateGlobalCartMeta(cart, parsed);
      return parsed;
    }

    const currentBody = currentDrawer.querySelector('.sidebar__body');
    const nextBody = nextDrawer.querySelector('.sidebar__body');

    if (currentBody && nextBody) {
      const scrollTop = currentBody.scrollTop;
      const currentSmile = currentBody.querySelector('[data-pw-smile-loyalty]');
      const importedBody = document.importNode(nextBody, true);
      const importedSmile = importedBody.querySelector('[data-pw-smile-loyalty]');

      // Smile is stateful and may already contain loaded SDK data. Move that
      // exact node into the fresh server markup instead of recreating it.
      if (currentSmile && importedSmile) importedSmile.replaceWith(currentSmile);

      currentBody.replaceWith(importedBody);
      importedBody.scrollTop = scrollTop;
    } else {
      // Defensive fallback for themes that render an older drawer structure.
      const currentItems = currentDrawer.querySelector('.cart__items');
      const nextItems = nextDrawer.querySelector('.cart__items');
      if (currentItems && nextItems) currentItems.innerHTML = nextItems.innerHTML;

      const currentShipping = currentDrawer.querySelector('[data-pw-cart-shipping]');
      const nextShipping = nextDrawer.querySelector('[data-pw-cart-shipping]');
      if (currentShipping && nextShipping) {
        currentShipping.replaceWith(document.importNode(nextShipping, true));
      }

      const currentSummary = currentDrawer.querySelector('[data-pw-cart-summary]');
      const nextSummary = nextDrawer.querySelector('[data-pw-cart-summary]');
      if (currentSummary && nextSummary) {
        currentSummary.replaceWith(document.importNode(nextSummary, true));
      }
    }

    const currentFooter = currentDrawer.querySelector('.sidebar__footer');
    const nextFooter = nextDrawer.querySelector('.sidebar__footer');
    if (currentFooter && nextFooter) currentFooter.replaceWith(document.importNode(nextFooter, true));
    const currentConfig = currentDrawer.querySelector('[data-pw-cart-drawer-config]');
    const nextConfig = nextDrawer.querySelector('[data-pw-cart-drawer-config]');
    if (currentConfig && nextConfig) currentConfig.textContent = nextConfig.textContent;
    if (window.PWCartDrawer?.reset) window.PWCartDrawer.reset(currentDrawer);

    currentDrawer.querySelectorAll('.cart-view-button').forEach((duplicate) => duplicate.remove());

    const currentHolder = currentDrawer.querySelector('.cart-holder');
    const nextHolder = nextDrawer.querySelector('.cart-holder');
    if (currentHolder && nextHolder) {
      currentHolder.dataset.items = nextHolder.dataset.items || '0';
    }

    initializeDrawer(currentDrawer);
    currentDrawer.classList.remove('pw-cart-needs-repair');
    updateGlobalCartMeta(cart, parsed);
    return parsed;
  };

  const renderMainCart = (html, cart) => {
    const parsed = parseSection(html);
    if (!parsed) return null;

    const currentMain = document.querySelector('#main-cart');
    const nextMain = parsed.querySelector('#main-cart') || parsed.body;
    if (!currentMain || !nextMain) return parsed;

    const currentItems = currentMain.querySelector('.cart__items');
    const nextItems = nextMain.querySelector('.cart__items');
    if (currentItems && nextItems) currentItems.innerHTML = nextItems.innerHTML;

    const currentSubtotal = currentMain.querySelector('#AjaxCartSubtotal');
    const nextSubtotal = nextMain.querySelector('#AjaxCartSubtotal');
    if (currentSubtotal && nextSubtotal) currentSubtotal.replaceWith(document.importNode(nextSubtotal, true));
    for (const selector of ['[data-pw-cart-page-loyalty]', '[data-pw-cart-page-discounts]']) {
      const current = currentMain.closest('.shopify-section')?.querySelector(selector);
      const next = parsed.querySelector(selector);
      if (current && next) current.hidden = next.hidden;
    }

    updateGlobalCartMeta(cart, parsed);
    return parsed;
  };

  const renderSections = (sections, cart) => {
    if (!sections || typeof sections !== 'object') {
      updateGlobalCartMeta(cart, null);
      return;
    }

    const drawerHTML = sections[sectionNames.drawer];
    const mainHTML = sections[sectionNames.main];
    const sourceDocument = drawerHTML
      ? renderDrawer(drawerHTML, cart)
      : mainHTML
        ? renderMainCart(mainHTML, cart)
        : null;

    if (drawerHTML && mainHTML) renderMainCart(mainHTML, cart);
    updateGlobalCartMeta(cart, sourceDocument);
  };

  const emitUpdated = (cart, source = 'cart') => {
    const detail = { cart, source };
    document.dispatchEvent(new CustomEvent('pw:cart:updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart-updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  };

  const enqueue = (task) => {
    const run = mutationQueue.then(task, task);
    mutationQueue = run.catch(() => undefined);
    return run;
  };

  const fetchCart = () => requestJSON(route('cart.js'), { cache: 'no-store' });

  const refresh = ({ openDrawer = false, recalculate = false } = {}) => enqueue(async () => {
    if (recalculate) {
      const cart = await requestJSON(route('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: {}, sections: [sectionNames.drawer, sectionNames.main], sections_url: window.location.pathname })
      });
      renderSections(cart.sections, cart);
      emitUpdated(cart, 'recalculate');
      if (openDrawer) document.querySelector('[data-js-site-cart-sidebar]')?.show?.();
      return cart;
    }
    const sectionQuery = encodeURIComponent(`${sectionNames.drawer},${sectionNames.main}`);
    const [cart, sectionsResponse] = await Promise.all([
      fetchCart(),
      fetch(`${route('')}?sections=${sectionQuery}`, {
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Section request failed (${response.status})`);
        return response.json();
      })
    ]);

    renderSections(sectionsResponse, cart);
    emitUpdated(cart, 'refresh');

    const recommendations = document.querySelector('cart-recommendations');
    if (recommendations && typeof recommendations.generateRecommendations === 'function') {
      recommendations.replaceChildren();
      recommendations.generateRecommendations();
    }

    if (openDrawer) {
      const sidebar = document.querySelector('[data-js-site-cart-sidebar]');
      if (sidebar && typeof sidebar.show === 'function') sidebar.show();
    }

    return cart;
  });

  window.PWCart = Object.assign(window.PWCart || {}, {
    sectionNames,
    route,
    requestJSON,
    fetchCart,
    enqueue,
    renderSections,
    updateGlobalCartMeta,
    emitUpdated,
    refresh
  });

  window.refreshCart = (options) => window.PWCart.refresh({
    openDrawer: !options || options.openDrawer !== false
  });

  let legacyRepairTimer = null;
  let legacyRepairInFlight = false;

  const drawerNeedsRepair = (drawer) => {
    if (!drawer) return false;

    const summary = drawer.querySelector('[data-pw-cart-summary]');
    const hasCanonicalSummary = summary
      && summary.querySelector('.cart__summary-row--subtotal')
      && summary.querySelector('.cart-summary__view-button');

    if (!hasCanonicalSummary) return true;

    const holder = drawer.querySelector('.cart-holder');
    const hasItems = Number.parseInt(holder && holder.dataset.items, 10) > 0;
    const shippingText = drawer.querySelector('[data-pw-cart-shipping] .cart-notice__text');
    const normalizedShippingText = shippingText
      ? shippingText.textContent.replace(/\s+/g, ' ').trim()
      : '';

    return hasItems && /^(?:(?:€|eur)\s*)?0(?:[,.]00)?$/i.test(normalizedShippingText);
  };

  const repairLegacyDrawer = () => {
    if (legacyRepairInFlight || typeof window.fetch !== 'function') return;

    const drawer = document.querySelector('.pw-cart-drawer');
    if (!drawer || !drawerNeedsRepair(drawer)) {
      if (drawer) drawer.classList.remove('pw-cart-needs-repair');
      return;
    }

    drawer.classList.add('pw-cart-needs-repair');
    legacyRepairInFlight = true;

    window.PWCart.refresh({ openDrawer: false })
      .catch((error) => {
        console.warn('Cart drawer: legacy update repair failed', error);
      })
      .finally(() => {
        legacyRepairInFlight = false;
        const latestDrawer = document.querySelector('.pw-cart-drawer');
        if (latestDrawer && drawerNeedsRepair(latestDrawer)) {
          window.clearTimeout(legacyRepairTimer);
          legacyRepairTimer = window.setTimeout(repairLegacyDrawer, 1200);
        }
      });
  };

  const scheduleLegacyDrawerRepair = () => {
    const drawer = document.querySelector('.pw-cart-drawer');
    if (!drawer || !drawerNeedsRepair(drawer)) return;

    drawer.classList.add('pw-cart-needs-repair');
    window.clearTimeout(legacyRepairTimer);
    legacyRepairTimer = window.setTimeout(repairLegacyDrawer, 0);
  };

  const observeLegacyDrawerUpdates = () => {
    const host = document.querySelector('[data-js-site-cart-sidebar]');
    if (!host || host.dataset.pwCartRepairObserver === 'true') return;

    host.dataset.pwCartRepairObserver = 'true';
    const observer = new MutationObserver(scheduleLegacyDrawerRepair);
    observer.observe(host, { childList: true, characterData: true, subtree: true });
    scheduleLegacyDrawerRepair();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeLegacyDrawerUpdates, { once: true });
  } else {
    observeLegacyDrawerUpdates();
  }

  document.addEventListener('pw:cart-drawer:rendered', observeLegacyDrawerUpdates);
  document.addEventListener('cart-updated', scheduleLegacyDrawerRepair);
  document.addEventListener('cart:updated', scheduleLegacyDrawerRepair);

  class PWCartForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pwCartFormReady === 'true') return;
      this.dataset.pwCartFormReady = 'true';
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.onSubmit = this.handleSubmit.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);

      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      delete this.dataset.pwCartFormReady;
    }

    handleSubmit(event) {
      event.preventDefault();
      const activeInput = this.querySelector('input.qty:focus');
      if (activeInput) this.commitInput(activeInput);
    }

    handleClick(event) {
      const remove = event.target.closest('.remove');
      if (!remove || !this.contains(remove)) return;
      event.preventDefault();
      const item = remove.closest('[data-js-cart-item]');
      if (item) this.updateCartQty(item, 0, { allowRemove: true });
    }

    handleChange(event) {
      const input = event.target.closest('input.qty');
      if (!input || !this.contains(input)) return;
      this.commitInput(input);
    }

    handleKeydown(event) {
      const input = event.target.closest('input.qty');
      if (!input || event.key !== 'Enter') return;
      event.preventDefault();
      this.commitInput(input);
    }

    commitInput(input) {
      const item = input.closest('[data-js-cart-item]');
      if (!item) return;
      // Queued rather than dropped: see the note in updateCartQty.
      this.updateCartQty(item, input.value, { allowRemove: false });
    }

    normalizeQuantity(item, requested, allowRemove) {
      const input = item.querySelector('input.qty');
      const step = Math.max(1, Number.parseInt(
        (input && input.dataset.quantityStep)
          || item.dataset.quantityStep
          || item.dataset.stepRule
          || (input && input.getAttribute('step'))
          || '1',
        10
      ) || 1);
      const minimum = step > 1 ? step : 1;
      let quantity = Number.parseInt(requested, 10);

      if (allowRemove && quantity === 0) return 0;
      if (!Number.isFinite(quantity)) quantity = minimum;
      quantity = Math.max(minimum, quantity);

      if (step > 1 && quantity % step !== 0) {
        quantity = Math.ceil(quantity / step) * step;
      }

      return quantity;
    }

    updateCartQty(item, requestedQuantity, { allowRemove = false } = {}) {
      if (!item) return Promise.resolve(null);

      // Do NOT drop the click while a previous change is in flight. PWCart.enqueue
      // already serialises mutations, so rapid clicks can safely queue. Returning
      // early here silently discarded every click made during the request +
      // re-render window, leaving the cart on a higher quantity than the one the
      // shopper clicked to (and the free shipping notice correct for that cart,
      // which reads as a stale notice).
      const input = item.querySelector('input.qty');
      const previousQuantity = input ? input.value : '';
      const quantity = this.normalizeQuantity(item, requestedQuantity, allowRemove);
      if (input) input.value = String(quantity);

      item.dataset.updating = 'true';
      this.form.classList.add('processing');
      this.clearAlert();

      return window.PWCart.enqueue(async () => {
        const payload = {
          quantity,
          sections: [sectionNames.drawer, sectionNames.main],
          sections_url: window.location.pathname
        };

        if (item.dataset.id) payload.id = item.dataset.id;
        else payload.line = Number.parseInt(item.dataset.line, 10);

        const cart = await requestJSON(route('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        renderSections(cart.sections, cart);
        emitUpdated(cart, quantity === 0 ? 'remove' : 'quantity');
        return cart;
      }).catch((error) => {
        if (input && document.contains(input)) input.value = previousQuantity;
        this.showAlert(error.message || this.defaultErrorMessage());
        return null;
      }).finally(() => {
        delete item.dataset.updating;
        if (this.form) this.form.classList.remove('processing');
      });
    }

    clearAlert() {
      const alert = this.querySelector('.alert[data-pw-cart-alert]');
      if (alert) alert.remove();
    }

    showAlert(message) {
      if (!this.form) return;
      this.clearAlert();
      const alert = document.createElement('span');
      alert.className = 'alert alert--error';
      alert.dataset.pwCartAlert = 'true';
      alert.setAttribute('role', 'alert');
      alert.textContent = message;
      this.form.prepend(alert);
    }

    defaultErrorMessage() {
      return window.KROWN && window.KROWN.settings && window.KROWN.settings.locales
        ? window.KROWN.settings.locales.cart_general_error
        : 'De winkelwagen kon niet worden bijgewerkt. Probeer het opnieuw.';
    }
  }

  if (!customElements.get('cart-form')) {
    customElements.define('cart-form', PWCartForm);
  }

  class PWCartProductQuantity extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pwQuantityReady === 'true') return;
      this.dataset.pwQuantityReady = 'true';
      this.input = this.querySelector('input.qty');
      this.minus = this.querySelector('.qty-minus');
      this.plus = this.querySelector('.qty-plus');
      this.onMinus = (event) => this.change(event, -1);
      this.onPlus = (event) => this.change(event, 1);

      if (this.minus) this.minus.addEventListener('click', this.onMinus, true);
      if (this.plus) this.plus.addEventListener('click', this.onPlus, true);
      this.updateButtons();
    }

    disconnectedCallback() {
      if (this.minus) this.minus.removeEventListener('click', this.onMinus, true);
      if (this.plus) this.plus.removeEventListener('click', this.onPlus, true);
      delete this.dataset.pwQuantityReady;
    }

    getRules() {
      const item = this.closest('[data-js-cart-item]');
      const step = Math.max(1, Number.parseInt(
        (this.input && this.input.dataset.quantityStep)
          || (item && item.dataset.quantityStep)
          || (item && item.dataset.stepRule)
          || (this.input && this.input.getAttribute('step'))
          || '1',
        10
      ) || 1);
      const declaredMin = Number.parseInt(this.input && this.input.min, 10);
      const minimum = step > 1
        ? Math.max(step, Number.isFinite(declaredMin) ? declaredMin : step)
        : 0;
      const declaredMax = Number.parseInt(this.input && this.input.max, 10);
      return {
        step,
        minimum,
        maximum: Number.isFinite(declaredMax) ? declaredMax : Infinity
      };
    }

    change(event, direction) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (!this.input || event.currentTarget.disabled) return;

      const item = this.closest('[data-js-cart-item]');
      const cartForm = this.closest('cart-form');
      if (!item || !cartForm || typeof cartForm.updateCartQty !== 'function') return;

      const rules = this.getRules();
      let current = Number.parseInt(this.input.value, 10);
      if (!Number.isFinite(current)) current = rules.step > 1 ? rules.minimum : 1;

      let next;
      if (rules.step > 1 && current % rules.step !== 0) {
        next = direction > 0
          ? Math.ceil(current / rules.step) * rules.step
          : Math.max(rules.minimum, Math.floor(current / rules.step) * rules.step);
      } else {
        next = current + (direction * rules.step);
      }

      next = Math.max(rules.minimum, Math.min(rules.maximum, next));
      if (next === current) return;

      this.input.value = String(next);
      this.updateButtons(next);
      cartForm.updateCartQty(item, next, { allowRemove: next === 0 });
    }

    updateButtons(value) {
      if (!this.input) return;
      const rules = this.getRules();
      const current = Number.parseInt(
        typeof value === 'undefined' ? this.input.value : value,
        10
      );
      if (!Number.isFinite(current)) return;

      if (this.minus) {
        const disabled = rules.step > 1 && current <= rules.minimum;
        this.minus.disabled = disabled;
        this.minus.classList.toggle('disabled', disabled);
      }

      if (this.plus) {
        const disabled = current + rules.step > rules.maximum;
        this.plus.disabled = disabled;
        this.plus.classList.toggle('disabled', disabled);
      }
    }
  }

  if (!customElements.get('cart-product-quantity')) {
    customElements.define('cart-product-quantity', PWCartProductQuantity);
  }

  class PWCartNote extends HTMLElement {
    constructor() {
      super();
      this.inflightController = null;
    }

    save(value) {
      if (this.inflightController) this.inflightController.abort();
      this.inflightController = new AbortController();
      const controller = this.inflightController;

      return requestJSON(route('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: value }),
        signal: controller.signal,
        keepalive: true
      }).catch((error) => {
        if (error && error.name === 'AbortError') return null;
        throw error;
      }).finally(() => {
        if (this.inflightController === controller) this.inflightController = null;
      });
    }

    flushPendingSave() {
      const textarea = this.querySelector('textarea');
      return textarea ? this.save(textarea.value) : Promise.resolve(null);
    }
  }

  if (!customElements.get('cart-note')) {
    customElements.define('cart-note', PWCartNote);
  }
})();



